import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { type FarmlandDetail } from "@/features/map/types/farmland";

interface Props {
  open: boolean;
  detail: FarmlandDetail | null;
  loading: boolean;
  error: boolean;
  onClose: () => void;
}

interface DetailRow {
  label: string;
  value: React.ReactNode;
}

const numberFormatter = new Intl.NumberFormat("ja-JP", {
  maximumFractionDigits: 2,
});

const displayValue = (value: React.ReactNode): React.ReactNode => {
  return value === null || value === undefined || value === "" ? "-" : value;
};

const formatArea = (value: number | null): string | null => {
  return value === null ? null : numberFormatter.format(value);
};

const formatNameOrCode = (
  name: string | null,
  code: string | null
): string | null => {
  return name ?? code;
};

const DetailSection: React.FC<{ title: string; rows: DetailRow[] }> = ({
  title,
  rows,
}) => (
  <Box>
    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Stack spacing={1.25}>
      {rows.map((row) => (
        <Box key={row.label}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            {row.label}
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
            {displayValue(row.value)}
          </Typography>
        </Box>
      ))}
    </Stack>
  </Box>
);

export const FarmlandDetailDrawer: React.FC<Props> = ({
  open,
  detail,
  loading,
  error,
  onClose,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 440 },
          maxWidth: "100vw",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6">農地詳細</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {detail?.daichoId ?? ""}
            </Typography>
          </Box>
          <Tooltip title="閉じる">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          {loading && (
            <Box
              sx={{
                height: 240,
                display: "grid",
                placeItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error">農地詳細の取得に失敗しました。</Alert>
          )}

          {!loading && !error && detail && (
            <Stack spacing={3}>
              <DetailSection
                title="基本情報"
                rows={[
                  {
                    label: "所在・地番",
                    value:
                      detail.address ?? `${detail.location}${detail.chiban}`,
                  },
                  { label: "都道府県", value: detail.prefectureName },
                  { label: "市区町村", value: detail.cityName },
                  { label: "所在地", value: detail.location },
                  { label: "地番", value: detail.chiban },
                  { label: "地目", value: detail.landCategory },
                  { label: "面積（m²）", value: formatArea(detail.area) },
                  { label: "権利設定状況", value: detail.rightsSetupStatus },
                  { label: "有効期限", value: detail.expiredAt },
                  { label: "緯度", value: detail.latitude },
                  { label: "経度", value: detail.longitude },
                ]}
              />

              <Divider />

              <DetailSection
                title="農地情報"
                rows={[
                  {
                    label: "所管農業委員会等",
                    value: formatNameOrCode(
                      detail.farmCommitteeName,
                      detail.farmCommitteeCd
                    ),
                  },
                  { label: "都道府県コード", value: detail.todofukenCode },
                  { label: "市区町村コード", value: detail.shikuchosonCode },
                  { label: "大字コード", value: detail.oazaCode },
                  { label: "大字・小字以降の地番", value: detail.tiban },
                  { label: "台帳種別コード", value: detail.daichoShubetsuCd },
                  {
                    label: "地目",
                    value: formatNameOrCode(
                      detail.classificationOfLandName,
                      detail.classificationOfLandCode
                    ),
                  },
                  {
                    label: "登記簿面積（m²）",
                    value: formatArea(detail.areaOnRegistry),
                  },
                  {
                    label: "農振法区分",
                    value: formatNameOrCode(
                      detail.sectionOfNoushinhouName,
                      detail.sectionOfNoushinhouCode
                    ),
                  },
                  {
                    label: "都市計画法区分",
                    value: formatNameOrCode(
                      detail.sectionOfToshikeikakuhouName,
                      detail.sectionOfToshikeikakuhouCode
                    ),
                  },
                  {
                    label: "所有者の農地に関する意向",
                    value: formatNameOrCode(
                      detail.ownerFarmIntentionName,
                      detail.ownerFarmIntentionCode
                    ),
                  },
                  {
                    label: "権利の種類",
                    value: formatNameOrCode(
                      detail.kindOfRightName,
                      detail.kindOfRightCode
                    ),
                  },
                  { label: "存続期間（始期）", value: detail.commencementDate },
                  { label: "存続期間（終期）", value: detail.endStagesDate },
                  {
                    label: "農地中間管理権の状況",
                    value: formatNameOrCode(
                      detail.rightSettingContentsName,
                      detail.rightSettingContentsCode
                    ),
                  },
                  {
                    label: "知事裁定日",
                    value: detail.rightOfMiddleManagement,
                  },
                  {
                    label: "利用状況調査日",
                    value: detail.usageSituationInvestigationDate,
                  },
                  {
                    label: "遊休農地かどうか",
                    value: formatNameOrCode(
                      detail.usageSituationInvestigationResultName,
                      detail.usageSituationInvestigationResultCode
                    ),
                  },
                  {
                    label: "利用意向調査日",
                    value: detail.useIntentionInvestigationDate,
                  },
                  {
                    label: "遊休農地の所有者等の意向",
                    value: formatNameOrCode(
                      detail.ownerStatementIntentSurveyResultsName,
                      detail.ownerStatementIntentSurveyResultsCode
                    ),
                  },
                  {
                    label: "所有者等の確知の状況",
                    value: formatNameOrCode(
                      detail.useIntentionAscertainmentResultName,
                      detail.useIntentionAscertainmentResultCode
                    ),
                  },
                  {
                    label: "所有者等を確知できない旨の公示を行った日",
                    value: detail.publicNoticeDate,
                  },
                  {
                    label: "農地中間管理機構との協議の勧告日",
                    value: detail.recommendationContentDate,
                  },
                  { label: "措置命令日", value: detail.actionOrderDate },
                  {
                    label: "市町村長が措置を行う旨の公示を行った日",
                    value: detail.mayorPublicAnnouncementDate,
                  },
                  {
                    label: "ポリゴン区分",
                    value: formatNameOrCode(
                      detail.sectionOfPolygonName,
                      detail.sectionOfPolygonCode
                    ),
                  },
                ]}
              />
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
